import { defineRegistry } from '@json-render/react';
import { MarkdownText } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import FindingsCard from '@/blocks/blocks/findings-card';
import InvestigationSummary from '@/blocks/blocks/investigation-summary';
import ScreeningResults from '@/blocks/blocks/screening-results';
import StructuringChart from '@/blocks/blocks/structuring-chart';
import BarChart from '@/blocks/primitives/bar-chart';
import DataTable from '@/blocks/primitives/data-table';
import HorizontalBarChart from '@/blocks/primitives/horizontal-bar-chart';
import KVList from '@/blocks/primitives/kv-list';
import NetworkGraph from '@/blocks/primitives/network-graph';
import ScoreGauge from '@/blocks/primitives/score-gauge';
import StatRow from '@/blocks/primitives/stat-row';
import TagList from '@/blocks/primitives/tag-list';
import Timeline from '@/blocks/primitives/timeline';
import { catalog } from './catalog';

// NOTE: every component here must also render its {children}.
const wrap =
  <P extends object>(Component: React.ComponentType<P>, name: string) =>
  ({ props, children }: { props: unknown; children?: ReactNode }): ReactNode => (
    <>
      <ErrorBoundary fallback={null} onError={err => console.error(`[GenUi:${name}]`, err)}>
        <Component {...(props as P)} />
      </ErrorBoundary>
      {children}
    </>
  );

const { registry } = defineRegistry(catalog, {
  components: {
    Markdown: ({ props, children }) => (
      <>
        <div className="text-body-2 [&_h1]:!text-[15px] [&_h1]:!leading-[24px] [&_h1]:!font-[525] [&_h2]:!text-[15px] [&_h2]:!leading-[24px] [&_h2]:!font-[525] [&_h3]:!text-[15px] [&_h3]:!leading-[24px] [&_h3]:!font-[525] [&_h1+*]:!mt-4 [&_h2+*]:!mt-4 [&_h3+*]:!mt-4 [&_hr]:hidden">
          <MarkdownText>{props.content}</MarkdownText>
        </div>
        {children}
      </>
    ),

    KVList: wrap(KVList, 'KVList'),
    StatRow: wrap(StatRow, 'StatRow'),
    DataTable: wrap(DataTable, 'DataTable'),
    Timeline: wrap(Timeline, 'Timeline'),
    ScoreGauge: wrap(ScoreGauge, 'ScoreGauge'),
    NetworkGraph: wrap(NetworkGraph, 'NetworkGraph'),
    ScreeningResults: wrap(ScreeningResults, 'ScreeningResults'),
    InvestigationSummary: wrap(InvestigationSummary, 'InvestigationSummary'),
    StructuringChart: wrap(StructuringChart, 'StructuringChart'),
    BarChart: wrap(BarChart, 'BarChart'),
    HorizontalBarChart: wrap(HorizontalBarChart, 'HorizontalBarChart'),
    FindingsCard: wrap(FindingsCard, 'FindingsCard'),

    TagList: ({ props, children }) => (
      <>
        <ErrorBoundary fallback={null} onError={err => console.error('[GenUi:TagList]', err)}>
          <div className="w-full overflow-hidden rounded-default border border-solid border-tertiary bg-primary px-4 py-3">
            <TagList {...props} />
          </div>
        </ErrorBoundary>
        {children}
      </>
    ),
  },
});

export { registry };
